<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import SalesArchiveDialog from '../components/SalesArchiveDialog.vue';
import SalesFinalizeDialog from '../components/SalesFinalizeDialog.vue';
import SalesIndexContent from '../components/SalesIndexContent.vue';
import SalesIndexHeader from '../components/SalesIndexHeader.vue';
import SalesIndexNavigation from '../components/SalesIndexNavigation.vue';
import PostSaleDeedFeedbackModal from '../components/PostSaleDeedFeedbackModal.vue';
import { useSalesActions } from '../composables/useSalesActions';
import { useSalesDashboard } from '../composables/useSalesDashboard';
import { useSalesListPresentation } from '../composables/useSalesListPresentation';
import { usePostSaleDeedFeedback } from '../composables/usePostSaleDeedFeedback';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const filtersVisible = ref(false);
const sales = useSalesDashboard({
  route,
  router,
  t,
  notify: useAlert,
  currentUser,
});
const deedFeedback = usePostSaleDeedFeedback({ t });
deedFeedback.load();
const actions = useSalesActions({
  t,
  notify: useAlert,
  refresh: sales.refresh,
  reloadTransaction: sales.loadTransaction,
});

const { listTabs, showManager, sortVisible } = useSalesListPresentation({
  t,
  dashboard: sales.dashboard,
  activeList: sales.activeList,
  currentUser,
  accountId: route.params.accountId,
});
const canCreate = computed(
  () =>
    sales.dashboard.value.capability?.capabilities?.['sales.draft.save'] ===
    true
);
const firstName = computed(
  () => currentUser.value?.name?.split(' ')?.[0] || ''
);

const openSalesCreation = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas_nova',
    params: { accountId: route.params.accountId },
  });

const openPendingCharge = item =>
  router.push({
    name: 'operacao_imobiliaria_vendas_pendencia_cobranca',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: item.processo_id,
      installmentId: item.parcela_id,
    },
  });
const applySort = sort => sales.applyFilters({ ...sales.filters.value, sort });
const applyAddress = address =>
  sales.applyFilters({ ...sales.filters.value, ...address });
const removeFilter = ({ key, value }) => {
  const values = sales.filters.value[key] || [];
  sales.applyFilters({
    ...sales.filters.value,
    [key]: values.filter(item => String(item) !== String(value)),
  });
};
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="flex min-h-full min-w-0 overflow-hidden bg-[#F5F7F8]"
  >
    <div data-testid="sales-index-shell" class="flex min-h-0 min-w-0 flex-1">
      <SalesIndexNavigation
        :active-section="sales.activeSection.value"
        :active-list="sales.activeList.value"
        :can-create="canCreate"
        :list-tabs="listTabs"
        @select-section="sales.selectSection"
        @select-list="sales.selectList"
        @create="openSalesCreation"
      />

      <section class="min-w-0 flex-1 overflow-y-auto">
        <SalesIndexHeader
          v-if="sales.activeSection.value === 'vendas'"
          :active-list="sales.activeList.value"
          :list-tabs="listTabs"
          :loading="sales.loading.value || sales.loadingDashboard.value"
          :updated-at="sales.listUpdatedAt.value"
          @refresh="sales.refresh"
          @select-list="sales.selectList"
        />

        <SalesIndexContent
          :sales="sales"
          :actions="actions"
          :first-name="firstName"
          :list-tabs="listTabs"
          :show-manager="showManager"
          :sort-visible="sortVisible"
          :filters-visible="filtersVisible"
          @open-pending-charge="openPendingCharge"
          @apply-address="applyAddress"
          @toggle-filters="filtersVisible = !filtersVisible"
          @apply-sort="applySort"
          @remove-filter="removeFilter"
        />
      </section>

      <SalesArchiveDialog
        :show="Boolean(actions.archiveTarget.value)"
        :is-loading="actions.archiveSaving.value"
        @close="actions.closeArchive"
        @confirm="actions.confirmArchive"
      />
      <SalesFinalizeDialog
        :show="Boolean(actions.finalizeTarget.value)"
        :is-loading="actions.finalizeSaving.value"
        :transaction="actions.finalizeTarget.value"
        @close="actions.closeFinalize"
        @confirm="actions.confirmFinalize"
      />
      <PostSaleDeedFeedbackModal
        :catalog="deedFeedback.catalog.value"
        :confirmation="deedFeedback.confirmation.value"
        :error="deedFeedback.error.value"
        :loading="deedFeedback.loading.value"
        :open="deedFeedback.open.value"
        :pending="deedFeedback.pending.value"
        :process="deedFeedback.selected.value"
        :proof="deedFeedback.proof.value"
        :show-list="deedFeedback.showList.value"
        :submitting="deedFeedback.submitting.value"
        :uploading="deedFeedback.uploading.value"
        @back="deedFeedback.backToList"
        @confirmed="deedFeedback.continueAfterConfirmation"
        @select="deedFeedback.select"
        @submit="deedFeedback.submit"
        @upload="deedFeedback.upload"
      />
    </div>
  </LaisGamificationSurface>
</template>
